import React, { useState, useEffect } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Star,
  Download,
  Terminal,
  AlertTriangle,
  BookOpen,
  Copy,
  Check,
  Bookmark,
  BookmarkCheck,
  Share2,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';
import { isFavorite, addFavorite, removeFavorite } from '../utils/storage';
import { submitFeedback, getFeedbackStats } from '../api/feedbackApi';

interface ToolDetailCardProps {
  tool: any;
  isExpanded: boolean;
  onToggle: () => void;
  query?: string;  // 搜索词，用于反馈 API
  lang?: 'zh' | 'en';  // 当前语言
}

// 国际化标签映射
const i18n = {
  zh: {
    // 标签页
    tabUsage: '用途',
    tabDownload: '下载',
    tabInstall: '安装',
    tabIssues: '避坑',
    tabCommands: '常用命令',
    // 用途
    coreUsage: '核心用途',
    applicableScenarios: '适用场景',
    targetUsers: '目标用户',
    coreAdvantages: '核心优势',
    alternatives: '替代工具',
    // 下载
    domesticMirror: '国内镜像 (推荐)',
    highSpeed: '高速下载',
    officialDownload: '官方下载',
    copy: '复制',
    // 安装
    // 避坑
    problemSymptom: '问题现象：',
    solution: '解决方案：',
    preventionTips: '预防建议：',
    // 通用
    update: '更新：',
    favorite: '收藏',
    unfavorite: '取消收藏',
    share: '分享',
    expand: '展开',
    collapse: '收起',
    useful: '这个推荐有用',
    notUseful: '这个推荐没用'
  },
  en: {
    // 标签页
    tabUsage: 'Use Cases',
    tabDownload: 'Download',
    tabInstall: 'Installation',
    tabIssues: 'Common Issues',
    tabCommands: 'Commands',
    // 用途
    coreUsage: 'Core Usage',
    applicableScenarios: 'Applicable Scenarios',
    targetUsers: 'Target Users',
    coreAdvantages: 'Key Advantages',
    alternatives: 'Alternatives',
    // 下载
    domesticMirror: 'China Mirror (Recommended)',
    highSpeed: 'Fast Download',
    officialDownload: 'Official Download',
    copy: 'Copy',
    // 安装
    // 避坑
    problemSymptom: 'Symptom: ',
    solution: 'Solution: ',
    preventionTips: 'Prevention: ',
    // 通用
    update: 'Update: ',
    favorite: 'Favorite',
    unfavorite: 'Unfavorite',
    share: 'Share',
    expand: 'Expand',
    collapse: 'Collapse',
    useful: 'This recommendation is helpful',
    notUseful: 'This recommendation is not helpful'
  }
};

export default function ToolDetailCard({ tool, isExpanded, onToggle, query, lang = 'zh' }: ToolDetailCardProps) {
  const t = i18n[lang]; // 获取当前语言的翻译
  const [activeTab, setActiveTab] = useState<'usage' | 'download' | 'install' | 'issues' | 'commands'>('usage');
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [isFav, setIsFav] = useState(isFavorite(tool.name));

  // 反馈相关状态
  const [feedbackStats, setFeedbackStats] = useState({ up_count: 0, down_count: 0 });
  const [userFeedback, setUserFeedback] = useState<'up' | 'down' | null>(null);

  // 加载反馈统计
  useEffect(() => {
    if (tool.name && query) {
      getFeedbackStats(tool.name, query).then(stats => {
        if (stats) {
          setFeedbackStats(stats);
        }
      });
    }
  }, [tool.name, query]);

  // 处理反馈提交（乐观更新，立即响应）
  const handleFeedback = (type: 'up' | 'down') => {
    if (!query) return;

    // 如果已经点过相同的，直接返回
    if (userFeedback === type) return;

    // 乐观更新：立即更新UI，不需要等待API响应
    const oldFeedback = userFeedback;
    const oldStats = { ...feedbackStats };

    // 计算新的统计（如果之前点过相反的，需要减少之前的计数）
    let newStats = { ...feedbackStats };
    if (oldFeedback === 'up') {
      newStats.up_count--;
    } else if (oldFeedback === 'down') {
      newStats.down_count--;
    }

    // 增加新的计数
    if (type === 'up') {
      newStats.up_count++;
    } else {
      newStats.down_count++;
    }

    // 立即更新UI
    setFeedbackStats(newStats);
    setUserFeedback(type);

    // 异步提交API（不阻塞UI）
    submitFeedback(tool.name, query, type, lang).then(result => {
      if (!result.success) {
        // 如果失败，回滚到之前的状态
        setFeedbackStats(oldStats);
        setUserFeedback(oldFeedback);
      }
    }).catch(err => {
      console.error('反馈提交失败:', err);
      // 失败也回滚
      setFeedbackStats(oldStats);
      setUserFeedback(oldFeedback);
    });
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const handleFavoriteToggle = () => {
    if (isFav) {
      removeFavorite(tool.name);
    } else {
      addFavorite(tool.name);
    }
    setIsFav(!isFav);
  };

  // 分享功能
  const handleShare = () => {
    const shareUrl = `${window.location.origin}?q=${encodeURIComponent(tool.name)}`;
    const shareText = `${tool.name} - ${tool.corePositioning || tool.coreUsage || ''}`;

    if (navigator.share) {
      // 使用原生分享 API（移动端）
      navigator.share({
        title: tool.name,
        text: shareText,
        url: shareUrl
      }).catch(err => {
        // 用户取消或失败，降级到复制链接
        fallbackShare();
      });
    } else {
      // 降级到复制链接
      fallbackShare();
    }
  };

  const fallbackShare = () => {
    const shareUrl = `${window.location.origin}?q=${encodeURIComponent(tool.name)}`;
    navigator.clipboard.writeText(shareUrl);
    setCopiedText('share');
    setTimeout(() => setCopiedText(null), 2000);
  };

  return (
    <div className="bg-white dark:bg-[#2D2D3F] rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
      {/* 卡片头部 */}
      <div className="p-6 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
                {tool.name}
              </h2>
              {tool.officialAlias && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ({tool.officialAlias})
                </span>
              )}
              {tool.rating === 5 && (
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#00B42A] text-[#00B42A]" />
                  ))}
                </div>
              )}
            </div>
            {tool.corePositioning && (
              <p className="text-gray-600 dark:text-gray-300 mb-2">
                {tool.corePositioning}
              </p>
            )}
            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
              <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-[#165DFF] rounded">
                {tool.category}
              </span>
              {tool.updateInfo && (
                <>
                  <span>{t.update}{tool.updateInfo.updateDate}</span>
                  <span>{tool.updateInfo.latestVersion}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {/* 点赞/点踩按钮组 */}
            {query && (
              <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleFeedback('up')}
                  className={`
                    flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors
                    ${userFeedback === 'up'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }
                  `}
                  title={t.useful}
                >
                  <ThumbsUp className="w-4 h-4" />
                  <span className="text-xs">{feedbackStats.up_count}</span>
                </button>
                <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
                <button
                  onClick={() => handleFeedback('down')}
                  className={`
                    flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors
                    ${userFeedback === 'down'
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }
                  `}
                  title={t.notUseful}
                >
                  <ThumbsDown className="w-4 h-4" />
                  <span className="text-xs">{feedbackStats.down_count}</span>
                </button>
              </div>
            )}

            <button
              onClick={handleFavoriteToggle}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={isFav ? t.unfavorite : t.favorite}
            >
              {isFav ? (
                <BookmarkCheck className="w-5 h-5 text-[#165DFF]" />
              ) : (
                <Bookmark className="w-5 h-5 text-gray-400" />
              )}
            </button>
            <button
              onClick={handleShare}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t.share}
            >
              {copiedText === 'share' ? (
                <Check className="w-5 h-5 text-green-500" />
              ) : (
                <Share2 className="w-5 h-5 text-gray-400" />
              )}
            </button>
            <button
              onClick={onToggle}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={isExpanded ? t.collapse : t.expand}
            >
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 标签页导航 */}
      {isExpanded && (
        <>
          <div className="flex border-b border-gray-100 dark:border-gray-700 overflow-x-auto">
            {[
              { key: 'usage', label: t.tabUsage, icon: BookOpen },
              { key: 'download', label: t.tabDownload, icon: Download },
              { key: 'install', label: t.tabInstall, icon: Terminal },
              { key: 'issues', label: t.tabIssues, icon: AlertTriangle },
              { key: 'commands', label: t.tabCommands, icon: Terminal }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key as any)}
                className={`
                  flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap
                  ${
                    activeTab === key
                      ? 'text-[#165DFF] border-b-2 border-[#165DFF] bg-blue-50 dark:bg-blue-900/20'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {/* 标签页内容 */}
          <div className="p-6">
            {/* 用途 */}
            {activeTab === 'usage' && (
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
                    {t.coreUsage}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    {tool.coreUsage}
                  </p>
                </div>
                {tool.applicableScenarios && (
                  <div>
                    <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
                      {t.applicableScenarios}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-300">
                      {tool.applicableScenarios}
                    </p>
                  </div>
                )}
                {tool.targetUsers && (
                  <div>
                    <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
                      {t.targetUsers}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-300">
                      {tool.targetUsers}
                    </p>
                  </div>
                )}
                {tool.coreAdvantages && (
                  <div>
                    <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
                      {t.coreAdvantages}
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300">
                      {tool.coreAdvantages.map((adv: string, i: number) => (
                        <li key={i}>{adv}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {tool.alternatives && tool.alternatives.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
                      {t.alternatives}
                    </h4>
                    <div className="flex gap-2">
                      {tool.alternatives.map((alt: string, i: number) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm"
                        >
                          {alt}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 下载 */}
            {activeTab === 'download' && tool.downloadUrl && (
              <div className="space-y-4">
                {tool.downloadUrl.mirror && (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-800 dark:text-gray-100">
                        {t.domesticMirror}
                      </h4>
                      <span className="text-xs text-green-600 dark:text-green-400">
                        {t.highSpeed}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 rounded text-sm text-gray-700 dark:text-gray-300 overflow-x-auto">
                        {tool.downloadUrl.mirror}
                      </code>
                      <button
                        onClick={() => handleCopy(tool.downloadUrl.mirror, 'mirror')}
                        className="p-2 hover:bg-white dark:hover:bg-gray-800 rounded transition-colors"
                        title={t.copy}
                      >
                        {copiedText === 'mirror' ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                {tool.downloadUrl.official && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
                      {t.officialDownload}
                    </h4>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-900 rounded text-sm text-gray-700 dark:text-gray-300 overflow-x-auto">
                        {tool.downloadUrl.official}
                      </code>
                      <button
                        onClick={() => handleCopy(tool.downloadUrl.official, 'official')}
                        className="p-2 hover:bg-white dark:hover:bg-gray-900 rounded transition-colors"
                        title={t.copy}
                      >
                        {copiedText === 'official' ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 安装 */}
            {activeTab === 'install' && tool.installation && (
              <div className="space-y-4">
                {Object.entries(tool.installation).map(([os, steps]: [string, any]) => {
                  if (typeof steps === 'string') {
                    return (
                      <div key={os} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2 capitalize">
                          {os === 'ubuntu' ? 'Ubuntu' : os === 'centos' ? 'CentOS' : os === 'docker' ? 'Docker' : os}
                        </h4>
                        <div className="flex items-start gap-2">
                          <code className="flex-1 px-3 py-2 bg-gray-900 dark:bg-black rounded text-sm text-green-400 font-mono overflow-x-auto">
                            {steps}
                          </code>
                          <button
                            onClick={() => handleCopy(steps, `install-${os}`)}
                            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                            title={t.copy}
                          >
                            {copiedText === `install-${os}` ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-500" />
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  }
                  if (Array.isArray(steps) && steps.length > 0) {
                    return (
                      <div key={os} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3 capitalize">
                          {os === 'ubuntu' ? 'Ubuntu' : os === 'centos' ? 'CentOS' : os === 'macos' ? 'macOS' : os}
                        </h4>
                        <div className="space-y-2">
                          {steps.map((step: string, i: number) => (
                            <div key={i} className="flex items-start gap-2">
                              <span className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                                {i + 1}.
                              </span>
                              <code className="flex-1 px-3 py-2 bg-gray-900 dark:bg-black rounded text-sm text-green-400 font-mono overflow-x-auto">
                                {step}
                              </code>
                              <button
                                onClick={() => handleCopy(step, `${os}-${i}`)}
                                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                title={t.copy}
                              >
                                {copiedText === `${os}-${i}` ? (
                                  <Check className="w-4 h-4 text-green-500" />
                                ) : (
                                  <Copy className="w-4 h-4 text-gray-500" />
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            )}

            {/* 避坑指南 */}
            {activeTab === 'issues' && tool.commonIssues && (
              <div className="space-y-4">
                {tool.commonIssues.map((issue: any, i: number) => (
                  <div key={i} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-gray-800 dark:text-gray-100">
                            Top {issue.rank || i + 1}: {issue.problem}
                          </h4>
                          {issue.affectedRate && (
                            <span className="text-xs text-red-600 dark:text-red-400">
                              {issue.affectedRate}
                            </span>
                          )}
                        </div>
                        {issue.symptoms && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                            <span className="font-medium">{t.problemSymptom}</span>{issue.symptoms}
                          </p>
                        )}
                        <div className="bg-white dark:bg-gray-800 rounded p-3">
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-medium text-green-600 dark:text-green-400">
                              {t.solution}
                            </span>
                            {issue.solution}
                          </p>
                        </div>
                        {issue.preventionTips && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            💡 {t.preventionTips}{issue.preventionTips}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 常用命令 */}
            {activeTab === 'commands' && tool.commonCommands && (
              <div className="space-y-3">
                {tool.commonCommands.map((cmd: any, i: number) => {
                  const command = typeof cmd === 'string' ? cmd : cmd.command;
                  const description = typeof cmd === 'object' ? cmd.description : '';
                  
                  return (
                    <div key={i} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex items-start gap-2 mb-2">
                        <code className="flex-1 px-3 py-2 bg-gray-900 dark:bg-black rounded text-sm text-green-400 font-mono overflow-x-auto">
                          {command}
                        </code>
                        <button
                          onClick={() => handleCopy(command, `cmd-${i}`)}
                          className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                          title={t.copy}
                        >
                          {copiedText === `cmd-${i}` ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-500" />
                          )}
                        </button>
                      </div>
                      {description && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 pl-3">
                          {description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
